// Local do arquivo: src/components/Products/Financeiro/pages/CadastroDespesasPage.jsx

import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import {
  createDespesa,
  deleteDespesa,
  getCategoriasDespesas,
  getDespesas,
  listDespesasRecorrentes,
  updateDespesa,
  // --- AJUSTE: Removidas as funções de API ...Recorrente que não existem ---
} from '../services/api';
import {
  computeDespesaStatus,
  formatDateInput,
  inferDespesaTipo,
  parseDateInput,
} from './utils/despesas';

const emptyRecorrenteForm = {
  descricao: '',
  valor: '',
  data_vencimento: '',
  categoria_id: '',
  observacoes: '',
};

const emptyParceladaForm = {
  descricao: '',
  valor: '',
  data_vencimento: '',
  data_termino: '',
  categoria_id: '',
  observacoes: '',
};

const CadastroDespesasPage = () => {
  const [categorias, setCategorias] = useState([]);
  const [recorrentes, setRecorrentes] = useState([]);
  const [parceladas, setParceladas] = useState([]);

  const [modalType, setModalType] = useState('recorrente');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyRecorrenteForm);

  const categoriaMap = useMemo(
    () => new Map(categorias.map((c) => [c.id, c.nome])),
    [categorias]
  );

  const load = async () => {
    try {
      const [despesas, recorr, cats] = await Promise.all([
        getDespesas(),
        listDespesasRecorrentes(),
        getCategoriasDespesas(),
      ]);

      const naoRecorrentes = Array.isArray(despesas)
        ? despesas
            .filter((d) => !d.is_recorrente)
            .map((d) => ({ ...d, tipo: d.tipo || inferDespesaTipo(d) }))
        : [];

      setParceladas(naoRecorrentes.filter((d) => d.tipo === 'parcelada'));
      setRecorrentes(Array.isArray(recorr) ? recorr : []);
      setCategorias(Array.isArray(cats) ? cats : []);
    } catch (e) {
      console.error('Erro ao carregar despesas:', e);
    }
  };

  useEffect(() => { load(); }, []);

  const handleOpen = (type, despesa = null) => {
    setModalType(type);

    if (type === 'recorrente') {
      if (despesa) {
        setEditing(despesa);
        setForm({
          descricao: despesa.descricao || '',
          valor: despesa.valor || '',
          data_vencimento: (() => {
            const raw = despesa.data_vencimento || despesa.data_inicio;
            if (!raw) return '';
            const date = new Date(raw);
            return Number.isNaN(date.getTime()) ? '' : formatDateInput(date);
          })(),
          categoria_id: despesa.categoria_id || '',
          observacoes: despesa.observacoes || '',
        });
      } else {
        setEditing(null);
        setForm({ ...emptyRecorrenteForm });
      }
    } else {
      if (despesa) {
        setEditing(despesa);
        setForm({
          descricao: despesa.descricao || '',
          valor: despesa.valor || '',
          data_vencimento: (() => {
            const raw = despesa.data_vencimento;
            if (!raw) return '';
            const date = new Date(raw);
            return Number.isNaN(date.getTime()) ? '' : formatDateInput(date);
          })(),
          data_termino: (() => {
            const raw = despesa.data_termino;
            if (!raw) return '';
            const date = new Date(raw);
            return Number.isNaN(date.getTime()) ? '' : formatDateInput(date);
          })(),
          categoria_id: despesa.categoria_id || '',
          observacoes: despesa.observacoes || '',
        });
      } else {
        setEditing(null);
        setForm({ ...emptyParceladaForm });
      }
    }

    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (modalType === 'parcelada') {
      if (!form.data_vencimento || !form.data_termino) {
        alert('Informe as datas de início e término da parcela.');
        return;
      }

      const inicio = parseDateInput(form.data_vencimento);
      const fim = parseDateInput(form.data_termino);
      if (!inicio || !fim || inicio.getTime() > fim.getTime()) {
        alert('A data de término deve ser igual ou posterior à data de início.');
        return;
      }
    }

    const basePayload = {
      descricao: form.descricao,
      valor: form.valor,
      categoria_id: form.categoria_id || null,
      observacoes: form.observacoes || '',
    };

    let payload = {}; // Payload final a ser enviado

    try {
      if (modalType === 'recorrente') {
        // --- AJUSTE: Monta o payload para RECORRENTE ---
        payload = {
          ...basePayload,
          data_vencimento: form.data_vencimento || null,
          data_termino: null,      // Importante
          data_pagamento: null,
          is_recorrente: true,   // Importante
          recorrencia_tipo: 'mensal', // Você pode ajustar isso
          recorrencia_fim: null,
          tipo: null,              // O backend ignora isso, o que é bom
          status: 'pendente',      // O backend provavelmente vai lidar com isso
        };
        
      } else {
        // --- AJUSTE: Monta o payload para PARCELADA ---
        payload = {
          ...basePayload,
          data_vencimento: form.data_vencimento || null,
          data_termino: form.data_termino || null, // Importante
          data_pagamento: null,
          is_recorrente: false, // Importante
          recorrencia_tipo: null,
          recorrencia_fim: null,
          tipo: 'parcelada', // Importante
          status: computeDespesaStatus({
            data_vencimento: form.data_vencimento,
            data_pagamento: null,
            refDate: new Date(),
          }),
        };
      }
      
      // --- AJUSTE: Usa a mesma função para criar ou editar ---
      if (editing?.id) {
        await updateDespesa(editing.id, payload);
      } else {
        await createDespesa(payload);
      }

      handleClose();
      load();
    } catch (e) {
      alert('Não foi possível salvar a despesa.');
    }
  };

  // --- AJUSTE: Uma única função de delete ---
  const handleDelete = async (despesa) => {
    if (!despesa?.id) return;
    if (!window.confirm(`Excluir despesa "${despesa.descricao}"? Esta ação não pode ser desfeita.`)) return;

    try {
      await deleteDespesa(despesa.id); // <-- Deleta do lugar certo
      load();
    } catch (e) {
      alert('Não foi possível excluir a despesa.');
    }
  };


  const renderRecorrentes = () => (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Despesas Recorrentes</Typography>
          <Typography variant="body2" color="text.secondary">Cadastre valores que se repetem mensalmente.</Typography>
        </Box>
        <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen('recorrente')}>Nova recorrente</Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Descrição</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Dia de Vencimento</TableCell>
              <TableCell>Observações</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recorrentes.map((r) => {
              const day = (() => {
                if (r.dia_vencimento) return r.dia_vencimento;
                if (r.data_vencimento) {
                  const date = new Date(r.data_vencimento);
                  return Number.isNaN(date.getTime()) ? '-' : date.getUTCDate();
                }
                return '-';
              })();

              return (
                <TableRow key={r.id} hover>
                  <TableCell>{r.descricao}</TableCell>
                  <TableCell>{Number(r.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                  <TableCell>
                    {r.categoria_id ? <Chip size="small" label={categoriaMap.get(r.categoria_id) || '...'} /> : '-'}
                  </TableCell>
                  <TableCell>{day}</TableCell>
                  <TableCell>{r.observacoes || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton color="primary" onClick={() => handleOpen('recorrente', r)} aria-label="Editar despesa recorrente"><Edit /></IconButton>
                    {/* --- AJUSTE: Chamando a função correta --- */}
                    <IconButton color="error" onClick={() => handleDelete(r)} aria-label="Excluir despesa recorrente"><Delete /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {recorrentes.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  Nenhuma despesa recorrente cadastrada.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const renderParceladas = () => (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Despesas Parceladas</Typography>
          <Typography variant="body2" color="text.secondary">Defina aqui o valor base de cada parcelamento.</Typography>
        </Box>
        <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen('parcelada')}>Nova parcelada</Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Descrição</TableCell>
              <TableCell>Valor</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Início</TableCell>
              <TableCell>Término</TableCell>
              <TableCell>Observações</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parceladas.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>{p.descricao}</TableCell>
                <TableCell>{Number(p.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                <TableCell>
                  {p.categoria_id ? <Chip size="small" label={categoriaMap.get(p.categoria_id) || '...'} /> : '-'}
                </TableCell>
                <TableCell>
                  {p.data_vencimento ? new Date(p.data_vencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}
                </TableCell>
                <TableCell>
                  {p.data_termino ? new Date(p.data_termino).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}
                </TableCell>
                <TableCell>{p.observacoes || '-'}</TableCell>
                <TableCell align="right">
                  <IconButton color="primary" onClick={() => handleOpen('parcelada', p)} aria-label="Editar despesa parcelada"><Edit /></IconButton>
                  {/* --- AJUSTE: Chamando a função correta --- */}
                  <IconButton color="error" onClick={() => handleDelete(p)} aria-label="Excluir despesa parcelada"><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {parceladas.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  Nenhuma despesa parcelada cadastrada.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={1}>Cadastro de Despesas</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Organize aqui os lançamentos recorrentes e parcelados. As ocorrências mensais serão exibidas na página de Despesas Mensais.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          {renderRecorrentes()}
        </Grid>
        <Grid item xs={12}>
          {renderParceladas()}
        </Grid>
      </Grid>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {modalType === 'recorrente'
            ? (editing ? 'Editar despesa recorrente' : 'Nova despesa recorrente')
            : (editing ? 'Editar despesa parcelada' : 'Nova despesa parcelada')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Descrição" name="descricao" value={form.descricao} onChange={handleChange} fullWidth />
          <TextField label="Valor" name="valor" type="number" value={form.valor} onChange={handleChange} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Categoria</InputLabel>
            <Select name="categoria_id" label="Categoria" value={form.categoria_id} onChange={handleChange}>
              {categorias.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={modalType === 'recorrente' ? 'Primeiro vencimento' : 'Início do parcelamento'}
            name="data_vencimento"
            type="date"
            value={form.data_vencimento}
            onChange={handleChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          {modalType === 'parcelada' && (
            <TextField
              label="Término do parcelamento"
              name="data_termino"
              type="date"
              value={form.data_termino}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          )}
          <TextField
            label="Observações (opcional)"
            name="observacoes"
            value={form.observacoes}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CadastroDespesasPage;